import { Fragment, useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, SvgIcon, Switch, Tooltip } from "@mui/material";

import "./style.scss";
import { ReactComponent as CubeIcon } from "../../assets/cube.icon.svg";
import { ReactComponent as GridIcon } from "../../assets/grid.icon.svg";
import { ReactComponent as PlusIcon } from "../../assets/plus.icon.svg";
import { ReactComponent as DotsIcon } from "../../assets/dots.icon.svg";
import { ReactComponent as GearIcon } from "../../assets/gear.icon.svg";
import { ReactComponent as TrashIcon } from "../../assets/trash.icon.svg";
import { Chat } from "../../common/interfaces/chats/chat.interface";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import {
    isMobileDevice,
    filterMobileSupported
} from "../../common/utilities/platform/platform.util";
import { FirebaseAuthContainer } from "../../common/containers/auth/firebase-auth-container.util";
import {
    KnowledgeBaseMenu,
    KnowledgeBaseMenuItem,
    KnowledgeBaseMenuItemType
} from "../knowledge-base-menu/knowledge-base-menu.comp";
import { fetchAgentPicker } from "../../api/users/endpoints-referrers";
import {
    DropdownMenu,
    DropdownMenuItem,
    SettingMenu,
    SettingMenuType,
    SettingItemType
} from "../dropdown-menu/dropdown-menu.comp";

const ChatList = ({
    chats,
    settings,
    selectedChatId,
    collapsedType = false,
    showKBList = false,
    showAdminSettings = false,
    handleChatDeleting
}: {
    chats: Chat[];
    settings: DropdownMenuItem[];
    selectedChatId?: string;
    collapsedType?: boolean;
    showKBList?: boolean;
    showAdminSettings?: boolean;
    handleChatDeleting: (arg1: Chat) => void;
}) => {
    const navigate = useNavigate();
    const { setLeftMenuShown } = ThemeContainer.useContainer();
    const { user, knowledgeBases, updateUserKnowledgeBase, updateUserAgentSelection } =
        FirebaseAuthContainer.useContainer();
    const [kbList, setKBList] = useState<KnowledgeBaseMenuItem[]>([]);
    // "All assistants" toggle (orchestrator routing). The assistant selection
    // stays on a real KB — the toggle only sets/clears the routing override
    // (users.currentAgentUid), so branding and the new-chat screen always
    // belong to the selected assistant.
    const [agentPicker, setAgentPicker] = useState<{
        enabled: boolean;
        orchestrator: { uid: string; name: string } | null;
    } | null>(null);
    const [togglingAgent, setTogglingAgent] = useState<boolean>(false);
    const [showMoreAvailable, setShowMoreAvailable] = useState<boolean>(chats.length > 5);
    const [showMoreActive, setShowMoreActive] = useState<boolean>(true);
    let lastSectionDate: Date | null = null;

    useEffect(() => {
        if (!showKBList || !user?.companyId) return;
        fetchAgentPicker()
            .then(({ data }) => setAgentPicker(data))
            .catch(() => setAgentPicker(null));
    }, [showKBList, user?.companyId]);

    useEffect(() => {
        if (!showKBList) return;

        const items: KnowledgeBaseMenuItem[] = knowledgeBases.map(kb => ({
            type: KnowledgeBaseMenuItemType.stroke,
            icon: CubeIcon,
            title: kb.displayName,
            role: kb.role,
            kbId: kb.uid,
            logoUrl: kb.logoUrl,
            action: () => {
                updateUserKnowledgeBase(kb.uid).then(() => {
                    navigate(`/new-chat`);
                });
            }
        }));
        setKBList(items);
    }, [navigate, showKBList, knowledgeBases, updateUserKnowledgeBase]);

    const allAssistantsOn = !!user?.currentAgentUid;
    const showAllAssistantsToggle = !!(
        showKBList &&
        agentPicker?.enabled &&
        agentPicker.orchestrator
    );

    const handleAllAssistantsToggle = async (checked: boolean) => {
        if (!agentPicker?.orchestrator || togglingAgent) return;
        setTogglingAgent(true);
        try {
            await updateUserAgentSelection(checked ? agentPicker.orchestrator.uid : null);
        } finally {
            setTogglingAgent(false);
        }
    };

    useEffect(() => {
        setShowMoreAvailable(chats.length > 5);
    }, [chats]);

    const diffOfDays = (date1: Date, date2: Date) => {
        const millisecondsPerDay = 1000 * 60 * 60 * 24;

        return Math.floor((date1.getTime() - date2.getTime()) / millisecondsPerDay);
    };

    const formatStringDate = (date: Date) => {
        const today = new Date();

        return diffOfDays(today, date) < 1 && date.getDate() === today.getDate()
            ? "Today"
            : diffOfDays(today, date) < 2 &&
                (date.getDate() + 1 === today.getDate() ||
                    (today.getDate() === 1 && date.getMonth() + 1 === today.getMonth()))
              ? "Yesterday"
              : diffOfDays(today, date) < 7
                ? "Previous 7 days"
                : diffOfDays(today, date) < 30
                  ? "Previous 30 days"
                  : date.toLocaleString("en-us", {
                        month: "long",
                        year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined
                    });
    };

    const handleChatCreatingClick = () => {
        setLeftMenuShown(false);
    };

    const handleShowMoreClick = () => {
        setShowMoreActive(false);
    };

    return (
        <div className={`chat-list${collapsedType ? " chat-list--collapsed" : ""}`}>
            <div className="chat-list-header">
                {showKBList && (
                    <KnowledgeBaseMenu
                        className="chat-list-kb-item"
                        items={kbList}
                        currentKnowledgeBaseId={user?.currentKnowledgeBaseId}
                        collapsed={collapsedType}
                    />
                )}
                {showAllAssistantsToggle && (
                    <Tooltip title="Route questions across all your assistants" placement="right">
                        <label
                            className={`chat-list-all-assistants${
                                collapsedType ? " chat-list-all-assistants--collapsed" : ""
                            }`}
                        >
                            <Switch
                                size="small"
                                checked={allAssistantsOn}
                                disabled={togglingAgent}
                                inputProps={{ "aria-label": "Use All Assistants" }}
                                onChange={e => handleAllAssistantsToggle(e.target.checked)}
                            />
                            {!collapsedType && (
                                <span className="chat-list-all-assistants__label">
                                    Use All Assistants
                                </span>
                            )}
                        </label>
                    </Tooltip>
                )}
                {showAdminSettings && !isMobileDevice() && (
                    <>
                        <Link className="chat-list-header-link" to="/dashboard">
                            <SvgIcon
                                className="chat-list-header-link__icon chat-list-header-link__icon--stroke"
                                component={GridIcon}
                                inheritViewBox
                            />
                            <span className="chat-list-header-link__text">Dashboard</span>
                        </Link>
                        <div className="chat-list-separator"></div>
                    </>
                )}
                <Link
                    className="chat-list-header-link"
                    to={"/new-chat"}
                    onClick={handleChatCreatingClick}
                >
                    <SvgIcon
                        className="chat-list-header-link__icon"
                        component={PlusIcon}
                        inheritViewBox
                    />
                    <span className="chat-list-header-link__text">New Chat</span>
                </Link>
            </div>
            <div className="chat-list-separator"></div>
            <div className="chat-list-items">
                {(showMoreActive ? chats.slice(0, 5) : chats).map(chat => {
                    const currentDate = new Date(Date.parse(chat.createdAt));
                    const isSectionAvailable =
                        !lastSectionDate ||
                        formatStringDate(lastSectionDate) !== formatStringDate(currentDate);
                    if (isSectionAvailable) {
                        lastSectionDate = currentDate;
                    }
                    return (
                        <Fragment key={chat.roomId}>
                            {isSectionAvailable && (
                                <div className="chat-list-section">
                                    <span className="chat-list-section__text">
                                        {formatStringDate(currentDate)}
                                    </span>
                                </div>
                            )}
                            <div
                                className={`chat-list-item${selectedChatId === chat.roomId ? " chat-list-item--selected" : ""}`}
                            >
                                <Link
                                    className="chat-list-item__link"
                                    to={`/chat/${chat.roomId}`}
                                    onClick={() => {
                                        setLeftMenuShown(false);
                                    }}
                                >
                                    {chat.title}
                                </Link>
                                <SettingMenu
                                    className="chat-list-item__icon--more"
                                    type={SettingMenuType.more}
                                    ariaLabel="Chat options"
                                    actions={[
                                        {
                                            icon: TrashIcon,
                                            name: "Delete",
                                            type: SettingItemType.default,
                                            handler: () => {
                                                handleChatDeleting(chat);
                                            }
                                        }
                                    ]}
                                />
                            </div>
                        </Fragment>
                    );
                })}
                {showMoreAvailable && showMoreActive && (
                    <Button className="chat-list-show-more-link" onClick={handleShowMoreClick}>
                        <SvgIcon
                            className="chat-list-show-more-link__icon"
                            component={DotsIcon}
                            inheritViewBox
                        />
                        <span className="chat-list-show-more-link__title">Show more</span>
                    </Button>
                )}
            </div>
            <div className="chat-list-settings">
                <DropdownMenu
                    icon={GearIcon}
                    title={!collapsedType ? "Settings" : undefined}
                    ariaLabel="Settings"
                    actions={filterMobileSupported(settings)}
                />
            </div>
        </div>
    );
};

export default ChatList;
